import React, { useCallback, useEffect, useState } from "react";
import {
    Modal,
    Divider,
    Typography,
    Progress,
    Button,
    Radio,
    RadioChangeEvent,
    Space,
    Col,
    Row,
    Popconfirm,
    notification,
    Tooltip,
} from "antd";
import { WorkspacePremiumOutlined } from "@mui/icons-material";
import { IQuestionnaireInstance } from "@Models/IQuestionnaireDetails";
import blocksConverter from "@Components/shared/RichTextEditor/blocksConverter";
import { Fixed, useLocalStorage, useSearchParams } from "@Utils";

const { Title, Text } = Typography;

interface ICMEModalProps {
    isModalOpen: boolean;
    isCMEPublic: boolean;
    showContinueAnimation: boolean;
    showProgressAnimation: boolean;
    questionnaireInstance: IQuestionnaireInstance;
    hasVideo: boolean;

    handleExit: () => void;
    onAnswerQuestion: (questionId: string, choiceId: string) => Promise<void>;
    onComplete: () => Promise<string>;
}

/**
 * Finds the highest integer before a gap in a set of integers.
 * @param integerSet - A set of integers
 */
const highestBeforeGap = (integerSet: Set<number>): number => {
    // Convert set to array and sort it
    const sortedArr = Array.from(integerSet).sort((a, b) => a - b);

    // Find the first gap
    for (let i = 0; i < sortedArr.length - 1; i++) {
        if (sortedArr[i + 1] - sortedArr[i] > 1) {
            return sortedArr[i];
        }
    }

    // If no gap is found, return the last element
    return sortedArr[sortedArr.length - 1];
};

/**
 * Prepares a map of answers from a list of questions.
 * @param questions - The list of questions to prepare answers for.
 * @returns An object containing the index of the last answered question and a map of previously answered questions.
 */
const prepareAnswers = (questions: IQuestionnaireInstance["questions"]) => {
    const previouslyAnsweredQuestions = new Map<string, string>();
    const answeredQuestionSet = new Set<number>([-1]);

    questions.forEach((question, index) => {
        if (!!question.answer) {
            answeredQuestionSet.add(index);
            previouslyAnsweredQuestions.set(question.questionId, question.answer);
        }
    });

    // Just in case they somehow skipped a question
    const highestAnsweredIndex = highestBeforeGap(answeredQuestionSet);
    return { highestAnsweredIndex, previouslyAnsweredQuestions };
};

const CMEModal: React.FC<ICMEModalProps> = ({
    isModalOpen,
    isCMEPublic,
    showContinueAnimation,
    showProgressAnimation,
    questionnaireInstance: data,
    handleExit,
    hasVideo,
    onAnswerQuestion,
    onComplete,
}) => {
    // filter out questions that have no choices
    const questions = data.questions.filter((question) => question.choices.length);
    const { highestAnsweredIndex, previouslyAnsweredQuestions } = prepareAnswers(questions);
    const [answers, setAnswers] = useState<Map<string, string>>(previouslyAnsweredQuestions);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(highestAnsweredIndex);
    const [score, setScore] = useState<string>();
    const [showCompletePage, setShowCompletePage] = useState(false);
    const [searchParams] = useSearchParams();
    const [unfinishedVideoTooltip, setUnfinishedVideoTooltip] = useState(false);
    const urlParamActivityId = searchParams.get("activity");
    const userId = searchParams.get("user");

    const isScored = questions.some((question) => question.isScored);

    const [watchedProgressPercentage, , getItem] = useLocalStorage(`${urlParamActivityId}-${userId}`);

    useEffect(() => {
        isModalOpen && isCMEPublic && getItem(`${urlParamActivityId}-${userId}`);
    }, [isModalOpen]);

    const hasWholeVideoBeenWatched = watchedProgressPercentage?.value === 100;

    /**
     * Handles the selection of an answer for a question.
     * @param questionId The id of the question
     * @param event The selection of an answer
     */
    const handleOptionSelect = (questionId: string, event: RadioChangeEvent) => {
        if (typeof event.target.value !== "string") {
            return;
        }
        const value = event.target.value;

        setAnswers((prevAnswers) => {
            const newAnswers = new Map(prevAnswers);
            newAnswers.set(questionId, value);
            return newAnswers;
        });
    };

    const currentQuestion = questions[currentQuestionIndex];

    /**
     * Handles the submission of an answer for the current question.
     */
    const handleAnswerQuestion = useCallback(() => {
        const currentAnswer = answers.get(currentQuestion?.questionId);
        const previousAnswer = currentQuestion?.answer;

        if (currentAnswer && currentAnswer !== previousAnswer) {
            onAnswerQuestion(currentQuestion?.questionId, currentAnswer).catch((error) => {
                // eslint-disable-next-line no-console
                console.error(error);
            });
        }
    }, [answers, currentQuestion, onAnswerQuestion]);

    /**
     * Handles the navigation to the next question.
     */
    const handleNext = useCallback(() => {
        handleAnswerQuestion();
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }

        const modalBody = document.querySelector(".ant-modal-content");

        if (modalBody) {
            modalBody.scrollTop = 0;
        }
    }, [currentQuestionIndex, questions.length, handleAnswerQuestion]);

    /**
     * Handles the navigation to the previous question.
     */
    const handleBack = useCallback(() => {
        if (currentQuestionIndex > -1) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    }, [currentQuestionIndex]);

    /**
     * Handles the saving of the current progress and exits the questionnaire.
     */
    const handleSave = useCallback(() => {
        handleAnswerQuestion();
        handleExit();
    }, [handleAnswerQuestion, handleExit]);

    /**
     * Handles the completion of the questionnaire and shows the completion page.
     */
    const handleConfirm = useCallback(() => {
        onComplete()
            .then((score) => {
                setScore(score);
                setShowCompletePage(true);
            })
            .catch(() => {
                notification.error({
                    message: "Failed to complete the questionnaire",
                });
            });
    }, [onComplete]);

    /**
     * The buttons in the footer of the modal based on the current state of the questionnaire.
     */
    const renderModalFooter = () => {
        if (showContinueAnimation || showProgressAnimation || showCompletePage) {
            return null;
        }

        const continueButton = (text = "Continue") => {
            const isButtonDisabled = currentQuestion && !answers.has(currentQuestion?.questionId);
            return (
                <Tooltip title={isButtonDisabled ? "Please answer the current question to continue." : undefined}>
                    <span>
                        <Button onClick={handleNext} className="dark" disabled={isButtonDisabled}>
                            {text}
                        </Button>
                    </span>
                </Tooltip>
            );
        };

        if (currentQuestionIndex === -1) {
            return ["I agree that I have read the learning objectives", continueButton("Agree & Continue")];
        }

        return [
            <Button className="secondary border-grey" onClick={handleBack}>
                Back
            </Button>,
            currentQuestionIndex < questions.length - 1 ? (
                continueButton()
            ) : (
                <Popconfirm
                    title="Are you sure you want to submit your answers?"
                    okText="Yes"
                    cancelText="Cancel"
                    className="confirmation"
                    onConfirm={handleConfirm}
                    disabled={
                        (currentQuestion && !answers.has(currentQuestion?.questionId)) ||
                        (hasVideo && isCMEPublic && !hasWholeVideoBeenWatched)
                    }
                    cancelButtonProps={{ className: "secondary" }}
                >
                    <Tooltip
                        title={`Please finish watching the video to earn cme points. You have currently watched ${
                            typeof watchedProgressPercentage?.value === "number"
                                ? Math.round(watchedProgressPercentage?.value)
                                : 0
                        }% of the video.`}
                        visible={hasVideo && isCMEPublic && !hasWholeVideoBeenWatched ? unfinishedVideoTooltip : false}
                    >
                        <div
                            onMouseEnter={() => setUnfinishedVideoTooltip(true)}
                            onMouseLeave={() => setUnfinishedVideoTooltip(false)}
                        >
                            <Button
                                onClick={handleAnswerQuestion}
                                className="dark"
                                disabled={
                                    (currentQuestion && !answers.has(currentQuestion?.questionId)) ||
                                    (hasVideo && isCMEPublic && !hasWholeVideoBeenWatched)
                                }
                            >
                                Submit
                            </Button>
                        </div>
                    </Tooltip>
                </Popconfirm>
            ),
        ];
    };

    const renderModalContent = () => {
        if (showContinueAnimation) {
            return (
                <div className="questionnaire-content welcome-back-page" style={{ height: "100%" }}>
                    <WorkspacePremiumOutlined />
                    <Title level={4}>Welcome back.</Title>
                    <Title level={4}>Let's pick up where you left off</Title>
                </div>
            );
        } else if (showProgressAnimation) {
            return (
                <div
                    className="header progress"
                    style={{
                        height: "100%",
                        minHeight: "600px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                    }}
                >
                    <Row style={{ width: "100%" }}>
                        <Col span={8} style={{ height: "32px" }} />
                        <Col span={8} className="progress">
                            <Progress
                                percent={((currentQuestionIndex + 1) / questions.length) * 100}
                                style={{ width: "400px" }}
                                showInfo={false}
                            />
                        </Col>
                        <Col span={8} />
                    </Row>
                    <div className="titles">
                        {currentQuestionIndex === -1 ? (
                            <Text strong>Learning objectives</Text>
                        ) : (
                            <Text strong>Questions</Text>
                        )}
                        <Title level={4}>{data.title}</Title>
                    </div>
                </div>
            );
        } else if (showCompletePage) {
            return (
                <div className="questionnaire-content complete" style={{ justifyContent: "center" }}>
                    <WorkspacePremiumOutlined style={{ fontSize: "80px" }} />
                    {isScored ? <Title level={5}>You scored {Fixed(Number(score))}%</Title> : null}
                    <Title level={5}>You've successfully completed your CME assessment</Title>
                    <Button className="dark" onClick={handleSave} style={{ marginTop: "40px" }}>
                        Save & exit
                    </Button>
                </div>
            );
        } else {
            return (
                <div>
                    <div className="header">
                        <Row style={{ width: "100%" }}>
                            <Col span={8} />
                            <Col span={8} className="progress">
                                <Progress
                                    percent={((currentQuestionIndex + 1) / questions.length) * 100}
                                    style={{ width: "400px" }}
                                    showInfo={false}
                                />
                            </Col>
                            <Col span={8} className="exit">
                                <Button className="secondary border-grey" onClick={handleSave}>
                                    Save & exit
                                </Button>
                            </Col>
                        </Row>
                        <div className="titles">
                            {currentQuestionIndex === -1 ? (
                                <Text strong>Learning objectives</Text>
                            ) : (
                                <Text strong>Questions</Text>
                            )}
                            <Title level={4}>{data.title}</Title>
                        </div>
                        <Divider />
                    </div>

                    <div className="questionnaire-content questions">
                        {currentQuestionIndex === -1 ? (
                            blocksConverter({ data: data.learningObjectiveDetails.description })
                        ) : (
                            <>
                                <h3 style={{ marginBottom: "20px" }}>{currentQuestion.title}</h3>

                                <Radio.Group
                                    onChange={(e) => handleOptionSelect(currentQuestion.questionId, e)}
                                    value={answers.get(currentQuestion.questionId)}
                                >
                                    <Space
                                        direction={
                                            currentQuestion.questionType === "choice" ? "vertical" : "horizontal"
                                        }
                                        size="large"
                                    >
                                        {currentQuestion.choices.map((choice) => (
                                            <Radio
                                                key={choice.choiceId}
                                                value={choice.choiceId}
                                                style={{ width: "100%" }}
                                                className={
                                                    answers.get(currentQuestion.questionId) === choice.choiceId
                                                        ? "radio-border selected"
                                                        : "radio-border"
                                                }
                                            >
                                                {choice.choiceText}
                                            </Radio>
                                        ))}
                                    </Space>
                                </Radio.Group>
                            </>
                        )}
                    </div>
                </div>
            );
        }
    };

    //TODO: loading and error states and async close on save&exit
    return (
        <Modal
            className={currentQuestionIndex === -1 ? "cme-modal" : "cme-modal lo-page"}
            title=""
            closable={false}
            visible={isModalOpen}
            footer={renderModalFooter()}
        >
            {renderModalContent()}
        </Modal>
    );
};

export default CMEModal;
